/**
 * 个人页面视频列表组件
 * 复用Home的video-grid结构，支持「我的视频」与浏览历史两种模式
 */

import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth, type User } from '../utils/auth';
import type { ApiResponse, VideoDescriptor } from '../types/api';
import { VideoCard } from './VideoCard';
import { ConfirmDialog } from './ConfirmDialog';
import { useInfiniteObserver } from '../hooks/useInfiniteObserver';
import { videoListCache } from '../utils/cache';
import './MyVideoList.css';

interface MyVideoListProps {
  activeTab: 'management' | 'history';
  user: User | null;
  hasMore: boolean;
  loadingMore: boolean;
  onHasMoreChange: (hasMore: boolean) => void;
  onLoadingMoreChange: (loadingMore: boolean) => void;
  scrollElRef: React.RefObject<HTMLElement | null>;
  onEmptyStateChange?: (isEmpty: boolean) => void;
  onListLoadingChange?: (isLoading: boolean) => void;
}

interface MyVideoListRef {
  saveCurrentScrollTop: () => void;
}

interface VideoListResponse {
  list: VideoDescriptor[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

/**
 * 规范化视频数据，统一使用 capabilities.hasWordAlignment 格式
 */
function normalizeVideo(item: any): VideoDescriptor | null {
  // 支持两种后端返回格式：
  // 1) 直接 VideoDescriptor（包含 capabilities.hasWordAlignment）
  // 2) 浏览历史 wrapper：{ video: VideoDescriptor, progress: number, viewedAt: Date }
  if (!item) return null;

  // case A: already a VideoDescriptor
  if (item.capabilities && typeof item.capabilities.hasWordAlignment === 'boolean') {
    return item as VideoDescriptor;
  }

  // case B: wrapper { video: VideoDescriptor, progress: number, viewedAt: Date }
  if (item.video && item.video.capabilities && typeof item.video.capabilities.hasWordAlignment === 'boolean') {
    const videoDescriptor = item.video as VideoDescriptor;

    // 为浏览历史的视频添加观看进度信息
    if (typeof item.progress === 'number') {
      // 添加到 _meta 中，这样不会破坏现有的 VideoDescriptor 类型
      if (!videoDescriptor._meta) {
        videoDescriptor._meta = {} as NonNullable<VideoDescriptor['_meta']>;
      }
      videoDescriptor._meta.watchProgress = Math.max(0, Math.min(100, item.progress));
    }

    return videoDescriptor;
  }

  return null;
}

export const MyVideoList = forwardRef<MyVideoListRef, MyVideoListProps>(({ 
  activeTab,
  user,
  hasMore,
  loadingMore,
  onHasMoreChange,
  onLoadingMoreChange,
  scrollElRef,
  onEmptyStateChange,
  onListLoadingChange
}, ref) => {
  const [videos, setVideos] = useState<VideoDescriptor[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // 删除视频相关状态
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    videoId: string | null;
  }>({
    isOpen: false,
    videoId: null,
  });

  const navigate = useNavigate();
  const loadingRef = useRef<boolean>(false);
  const PAGE_SIZE = 20;
  const abortRef = useRef<AbortController | null>(null);
  const reqIdRef = useRef(0);
  // Secondary safety: ignore stale responses even if abort handling changes in the future.
  // reqIdRef increments at request start and responses check against current value before applying.
  // keep a ref of latest activeTab to hard-guard stale responses
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  // NOTE: The following ref + effects implement a strict "tab gating" policy:
  // - `activeTabRef` always holds the latest activeTab.
  // - Each request snapshots `tabAtRequest = activeTab` at send time.
  // - On response we compare `tabAtRequest !== activeTabRef.current` and drop stale responses.
  // This prevents any out-of-order or slow responses from a previous tab from contaminating
  // the currently visible list.
  // stable ref to call loading first page without causing activeTab effect to depend on loadVideos directly
  const loadFirstPageRef = useRef<() => void>(() => {});

  // 暴露方法给父组件，用于在tab切换前保存scrollTop
  useImperativeHandle(ref, () => ({
    saveCurrentScrollTop: () => {
      // no-op for hard-reload tab behavior
      return;
    }
  }));

  // 保持viewRef与组件状态同步
 

  // 通知父组件空状态变化
  useEffect(() => {
    const isEmpty = videos.length === 0 && !loading;
    onEmptyStateChange?.(isEmpty);
  }, [videos.length, loading, onEmptyStateChange]);

  // 通知父组件列表级别的加载状态（用于在父层展示居中加载UI）
  useEffect(() => {
    const isListLoading = loading && videos.length === 0;
    onListLoadingChange?.(isListLoading);
  }, [loading, videos.length, onListLoadingChange]);

  // loadVideos 函数声明（提前以避免useEffect的依赖问题）
  /**
   * loadVideos
   *
   * Concurrency & consistency rules (summary):
   * - append=true (loadMore): never abort previous requests; use loadingRef + onLoadingMoreChange to prevent duplicates.
   * - append=false (initial / tab switch): abort previous non-append request via abortRef, then create a new AbortController.
   * - Snapshot tabAtRequest before sending; after receiving, perform two gates before applying state:
   *    1) reqId check: ensure this is the latest request id for this component instance.
   *    2) tab check: ensure tabAtRequest === activeTabRef.current (hard gate).
   * - Always set UI loading state (onLoadingMoreChange/setLoading) BEFORE the network call so finally can reliably clear it.
   * - Only clear loadingRef and per-request-inflight flags when this response is still the latest (reqId matches).
   *
   * Dependency requirements:
   * - include `loadingMore` in dependencies because non-append branch guards on it.
   */
  const loadVideos = useCallback(async (page: number, append: boolean = false) => {
    console.log('[MyVideoList] loadVideos called:', { activeTab, page, append });
    // snapshot the tab at request time to prevent stale tab responses from landing
    const tabAtRequest = activeTab;
    // NOTE: AbortController is ONLY for non-append (initial/tab switch) requests.
    // NOTE: append(loadMore) must never be aborted, otherwise infinite scroll can flicker or stall.
    // For append (loadMore) requests we DO NOT abort previous requests to avoid
    // cancelling in-flight pagination (prevents flashing / incomplete loads).
    // For non-append (initial / tab) requests we abort previous to avoid duplicate effective responses.
    if (append) {
      // prevent duplicate append triggers (either component-level loadingRef or parent-provided loadingMore)
      if (loadingRef.current || loadingMore) return;
    } else {
      // prevent starting a non-append (fresh) request while a loadMore is in-flight
      if (loadingMore) return;
      // prevent duplicate non-append requests (debounce)
      if (loadingRef.current) {
        return;
      }
    }

    // 获取当前请求ID并递增（组件级）
    const reqId = ++reqIdRef.current;
    loadingRef.current = true;

    if (append) {
      onLoadingMoreChange(true);
    } else {
      setLoading(true);
    }

    try {
      const endpoint = tabAtRequest === 'management' ? '/my-videos' : '/user/history';
      console.log('[MyVideoList] Making API request:', `${API_BASE_URL}${endpoint}?page=${page}&pageSize=${PAGE_SIZE}`);
      const fetchOpts: RequestInit = {
        headers: getAuthHeaders(),
      };
      // do not attach AbortController signal for non-append requests to avoid cancelling in-flight requests;
      // rely on reqId and activeTabRef gating to ignore stale responses.
      const response = await fetch(`${API_BASE_URL}${endpoint}?page=${page}&pageSize=${PAGE_SIZE}`, fetchOpts);
      console.log('[MyVideoList] API response status:', response.status);

      if (response.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse<VideoListResponse> = await response.json();
      console.log('[MyVideoList] API response data:', data);
      if (!data.success || !data.data) {
        throw new Error(data.error || '获取视频列表失败');
      }

      // 检查是否是最新请求（防止竞态条件）
      if (reqId !== reqIdRef.current) {
        return; // 忽略过时的响应
      }

    // 如果发起请求时的 tab 与当前最新 activeTab 不一致，则忽略响应（硬门禁）
    if (tabAtRequest !== activeTabRef.current) {
      return;
    }

      const rawList = data.data.list || [];
      console.log('[MyVideoList] Raw list from API:', rawList);

      // 规范化数据，确保所有项目都是 VideoDescriptor
      const normalizedList: VideoDescriptor[] =
        rawList
          .map(normalizeVideo)
          .filter((v): v is VideoDescriptor => v !== null);
      console.log('[MyVideoList] Normalized list:', normalizedList);

      // 更新组件状态
      if (append) {
        setVideos(prev => {
          const newVideos = [...prev, ...normalizedList];
          return newVideos;
        });
      } else {
        setVideos(normalizedList);
      }

      // 更新分页状态
      const newPage = page;
      const newHasMore = data.data.hasMore;

      setCurrentPage(newPage);
      onHasMoreChange(newHasMore);

      // 更新分页状态已在 state/onHasMoreChange 中同步

    } catch (err) {
      // If aborted (only possible for non-append requests), silently return
      if (err instanceof Error && (err as any).name === 'AbortError') {
        return;
      }
      console.error('加载视频列表失败:', err);
      // 不立即清空现有列表，避免闪烁；父组件可选择显示错误
  } finally {
    // 只在当前请求仍然有效时才清理加载状态与防重标记
    if (reqId === reqIdRef.current) {
      if (append) {
        onLoadingMoreChange(false);
      } else {
        setLoading(false);
      }
      // clear component-level loading lock
      loadingRef.current = false;
    }

    // no-op: we no longer abort previous non-append requests; rely on reqId gating
  }
  }, [navigate, activeTab, onHasMoreChange, onLoadingMoreChange, loadingMore]);

  // keep a stable ref to trigger first page load; update the ref whenever loadVideos changes
  useEffect(() => {
    loadFirstPageRef.current = () => {
      loadVideos(1, false);
    };
  }, [loadVideos]);

  // 当 activeTab 变化时，重置分页并通过稳定引用加载第一页（避免 loadingMore 改变触发）
  useEffect(() => {
    console.log('[MyVideoList] activeTab changed:', activeTab);
    setCurrentPage(1);
    loadFirstPageRef.current();
  }, [activeTab]);

  // cleanup on unmount: abort any in-flight request
  useEffect(() => {
    const currentAbortRef = abortRef.current;
    return () => {
      try {
        currentAbortRef?.abort();
      } catch (error) {
        // AbortController.abort() 可能在某些情况下抛出错误，可以安全忽略
        // 但记录日志以便调试
        if (process.env.NODE_ENV === 'development') {
          console.warn('[MyVideoList] AbortController.abort() 失败:', error);
        }
      }
    };
  }, []);

  // Infinite scroll hook
  const onLoadMore = useCallback(() => {
    loadVideos(currentPage + 1, true);
  }, [currentPage, loadVideos]);

  const { sentinelRef } = useInfiniteObserver({
    enabled: true,
    hasMore,
    isLoading: loadingRef.current || loadingMore,
    onLoadMore,
    rootMargin: '100px',
    threshold: 0.1,
  });

  const handleEditVideo = (videoId: string) => {
    navigate(`/video/edit?videoId=${encodeURIComponent(videoId)}`);
  };

  const handleDeleteVideo = (videoId: string) => {
    setDeleteConfirmDialog({ isOpen: true, videoId });
  };

  const handleConfirmDelete = async () => {
    const videoId = deleteConfirmDialog.videoId;
    if (!videoId) return;

    setDeleteConfirmDialog({ isOpen: false, videoId: null });

    try {
      const response = await fetch(`${API_BASE_URL}/user/delete-video`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ videoId }),
      });

      if (response.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      const data: ApiResponse = await response.json();

      if (data.success) {
        // 从列表中移除
        setVideos(prevVideos => prevVideos.filter(v => v.videoId !== videoId));
        // 清空首页缓存，确保已删除的公开视频不再显示在首页
        videoListCache.clear();
      } else {
        window.alert(data.error || '删除失败');
      }
    } catch (err) {
      window.alert('网络错误，请稍后重试');
    } finally {
      // Delete operation completed
    }
  };

  // 找到要删除的视频名称
  const videoToDelete = deleteConfirmDialog.videoId
    ? videos.find(v => v.videoId === deleteConfirmDialog.videoId)
    : null;

  // 不在组件内部渲染首屏 loading，父组件负责空状态/加载居中展示

  const userRole = user?.role || 'user';
  const canCreateVideo =
    userRole === 'premium' || userRole === 'super' || userRole === 'admin';

  return (
    <>
      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen}
        title="确认删除视频"
        message={`确定要删除视频 "${videoToDelete?.name || videoToDelete?.videoId || '未知视频'}" 吗？`}
        confirmText="删除"
        cancelText="取消"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmDialog({ isOpen: false, videoId: null })}
      />

      {activeTab === 'management' && canCreateVideo && (
        <div className="my-videos-toolbar">
          <button
            type="button"
            className="my-videos-create-btn"
            onClick={() => navigate('/upload')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>新建视频</span>
          </button>
        </div>
      )}

      {videos.map((video) => {
        // 额外的安全检查，确保video不为undefined
        if (!video) {
          console.error('[MyVideoList] 发现undefined video，跳过渲染');
          return null;
        }
        return (
          <VideoCard
            key={video.videoId}
            video={video}
            mode={activeTab}
            onEdit={handleEditVideo}
            onDelete={handleDeleteVideo}
            showAdminActions={activeTab === 'management'}
          />
        );
      })}


      {/* 滚动加载触发器 - 只用于Intersection Observer，不渲染UI */}
      <div
        ref={sentinelRef}
        style={{ height: '1px' }}
      />
    </>
  );
});
